/**
 * The logging screen. This is the one that gets used standing at a rack with
 * one hand, so every decision here favours "fewest taps" over "most features".
 *
 * It reads entirely from Dexie via useLiveQuery, so it renders identically
 * whether or not there's a signal, and it updates by itself when a background
 * sync lands another device's changes.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'

import { distanceScaleOf, getMeta, measuresOf, setMeta, weightIsAdded,
  weightIsAssist,
         type Exercise, type Plan, type PlanDay, type PlanItem, type SetRow }
  from '../db'
import {
  getActiveSession,
  getExercises,
  getPlanDay,
  getPlans,
  getSessionSets,
  lastPerformance,
} from '../db/queries'
import {
  addRound,
  addSet,
  deleteSet,
  finishSession,
  startSession,
  updateSet,
} from '../db/mutations'
import { syncSoon } from '../sync/engine'
import * as notify from '../native/notify'
import { describeSets, suggestNext } from '../lib/progression'
import { entryHint } from '../lib/entryHint'
import { SIDES, SIDE_LABEL, carryFor, countRounds, formatRound, groupRounds, hasSides,
         logsSides, rowsOf, type Side } from '../lib/rounds'
import { prefillFrom, repsBand } from '../lib/prefill'
import {
  displayToKg,
  metresToDisplay,
  displayToMetres,
  formatWeight,
  maskDuration,
  normalizeDuration,
  parseDuration,
  gymFor,
  platesFor,
  DISTANCE_UNITS,
  type DistancePref,
  type DistanceUnit,
  type Unit,
} from '../lib/units'
import { formatSet } from '../lib/setFormat'
import { setDistanceOverride, unitFor, useDistanceOverrides } from '../lib/distance'
import { dayOf, startedAtFor, todayIso } from '../lib/day'
import { extraExerciseIds, firstRemaining, prescribedIdsOf }
  from '../lib/sessionQueue'
import { applyOrder, mergeOrder } from '../lib/reorder'
import { needsReveal } from '../lib/scroll'
import { parseIso } from '../lib/time'
import { RestTimer } from '../components/RestTimer'
import { WorkoutQueue } from '../components/WorkoutQueue'
import { ExercisePicker } from '../components/ExercisePicker'
import { SuggestWorkout } from '../components/SuggestWorkout'
import { ExerciseInfoPanel, hasInfo } from '../components/ExerciseInfo'
import { SetEditor } from '../components/SetEditor'

/**
 * Android 13+ needs POST_NOTIFICATIONS granted at runtime, and notify.ts asks
 * in context by design. "A workout is open" is that context — and it is the
 * last moment before a rest timer can arm. Asking later, once the timer is
 * counting, would inflate the rest by however long the dialog sat unanswered.
 *
 * Module-level so it survives the screen remounting between tabs. Asked once
 * per app run: the OS remembers a denial, and re-prompting every workout is
 * how you train someone to hit Deny reflexively.
 */
let restPermissionAsked = false

export function LogScreen({
  unit,
  distancePref,
  defaultRest,
  username,
}: {
  unit: Unit
  distancePref: DistancePref
  defaultRest: number
  username: string
}) {
  const session = useLiveQuery(getActiveSession, [])
  const exercises = useLiveQuery(getExercises, [], [] as Exercise[])
  const sets = useLiveQuery(
    () => (session ? getSessionSets(session.id) : Promise.resolve([])),
    [session?.id],
    [] as SetRow[],
  )

  const plans = useLiveQuery(getPlans, [], [] as Plan[])
  const planDay = useLiveQuery(
    () => getPlanDay(session?.plan_day_id ?? null),
    [session?.plan_day_id],
    null as PlanDay | null,
  )

  const [currentExerciseId, setCurrentExerciseId] = useState<number | null>(null)
  const [picking, setPicking] = useState(false)
  // Exercises added to this workout only. Kept in local meta keyed by session
  // rather than written to the plan: adding squats today must not put squats
  // in next Tuesday's prescription. They're purely a display aid — the real
  // record is the sets, which have never needed permission from a plan.
  const [extras, setExtras] = useState<number[]>([])
  // The same idea in the other direction: exercises taken out of this workout.
  // A plan prescribes for a gym you might not be standing in, and swapping one
  // exercise for another used to leave the original sitting in the queue for
  // ever, unfinishable. Stored the same way and for the same reason — removing
  // leg press today must not remove it from next Tuesday.
  const [removed, setRemoved] = useState<number[]>([])
  // Today's running order, as exercise ids. One array carries both lists — the
  // plan's and the ones added today — because they're disjoint and applyOrder
  // only looks at the ids in the list it's handed. Per-session for the third
  // time and the same reason: doing legs before pressing today is a fact about
  // today, not an edit to next Tuesday's prescription.
  const [order, setOrder] = useState<number[]>([])
  const [restKey, setRestKey] = useState(0)
  // Which day this workout happened on. Defaults to today; changing it is how
  // you record the session you did without your phone.
  const [startDate, setStartDate] = useState(todayIso)

  /** The panel (or the picker standing in for it) — what a tap should reveal. */
  const panelRef = useRef<HTMLDivElement | null>(null)

  const byId = useMemo(
    () => new Map(exercises.map((e) => [e.id, e])),
    [exercises],
  )

  // The recap list below renders several different exercises at once, so it
  // needs the whole override map rather than one exercise's answer.
  const overrides = useDistanceOverrides()

  // When a session already has sets, default to whatever was last worked on —
  // reopening the app mid-workout should land you where you left off, not on an
  // empty picker.
  useEffect(() => {
    if (currentExerciseId !== null) return
    if (sets.length > 0) {
      setCurrentExerciseId(sets[sets.length - 1].exercise_id)
      return
    }
    // Nothing logged yet, but the workout came from a plan — open on the first
    // prescribed exercise instead of an empty picker. The plan already answered
    // "what am I doing first", so asking again is a tap for no information.
    // Skipping the removed ones is what makes removal feel like removal: land
    // on one and it reappears as the thing you're apparently about to do.
    // Today's order, not the plan's: having dragged an exercise to the top, that
    // is the one the screen should open on.
    const queue = applyOrder(planDay?.items ?? [], (i) => i.exercise?.id, order)
    const first = firstRemaining(queue, removed)
    if (first !== null) {
      setCurrentExerciseId(first)
      return
    }
    // No plan, so nothing was prescribed — but exercises added to this workout
    // are still a queue, and the same rules apply. Landing here happens after a
    // removal clears the selection: without this the screen would drop back to
    // the picker with two perfectly good exercises still in the list.
    const added = applyOrder(extras, (id) => id, order)
                    .find((id) => !removed.includes(id))
    if (added !== undefined) setCurrentExerciseId(added)
  }, [sets, currentExerciseId, planDay, extras, removed, order])

  useEffect(() => {
    if (!session || restPermissionAsked) return
    restPermissionAsked = true
    void notify.requestPermission()
  }, [session?.id])

  // Bring the panel into view after a tap that selected an exercise.
  //
  // Only after a tap. currentExerciseId also changes when the screen opens on the
  // first prescribed exercise, when a removal picks the next one still standing,
  // and when a spoken "switch to incline press" lands — none of which should move
  // the page under someone who never asked for it.
  //
  // A counter, and it has to be a counter. The first version set a ref at the tap
  // and ran this effect on [currentExerciseId, picking], which failed on the most
  // ordinary case there is: tapping the exercise that was already selected. Both
  // setState calls got the value they already held, React bailed out of the
  // re-render, the effect never ran, and the tap did nothing — the exact bug this
  // effect exists to fix, still there for the row most likely to be tapped.
  //
  // It left the ref set, too. The next selection change from anywhere — a removal
  // picking the next exercise, a spoken switch — would consume that stale flag and
  // scroll the page for a tap that had happened minutes earlier.
  //
  // Incrementing always changes, so the effect always runs, and there is no flag
  // left lying around to be spent by something else.
  const [revealTick, setRevealTick] = useState(0)

  useEffect(() => {
    if (revealTick === 0) return          // mount, not a tap
    const el = panelRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    // The pinned rest timer covers the top of the screen while it counts, and
    // anything behind it is not visible however much of it is technically on
    // screen. Its own height is the honest measure of how much it hides.
    // Only while it is actually pinned: at rest it sits in the flow further up the
    // page and covers nothing. `top <= 1` is "stuck to the top edge".
    const timer = document.querySelector('[data-rest-timer]')
    const timerRect = timer?.getBoundingClientRect()
    const obscuredTop = timerRect && timerRect.top <= 1 ? timerRect.bottom : 0
    if (!needsReveal({
      top: rect.top,
      height: rect.height,
      viewportHeight: window.innerHeight,
      obscuredTop,
    })) return
    el.scrollIntoView({
      block: 'start',
      // Smooth says "the page moved" rather than teleporting, which matters when
      // you didn't ask for a scroll — but not to anyone who has asked the OS to
      // stop animating things.
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto' : 'smooth',
    })
  }, [revealTick])

  // Survives leaving the tab, and the app being killed mid-workout.
  useEffect(() => {
    if (!session) return
    let cancelled = false
    void getMeta<number[]>(extrasKey(session.id), []).then((ids) => {
      if (!cancelled) setExtras(ids)
    })
    void getMeta<number[]>(removedKey(session.id), []).then((ids) => {
      if (!cancelled) setRemoved(ids)
    })
    void getMeta<number[]>(orderKey(session.id), []).then((ids) => {
      if (!cancelled) setOrder(ids)
    })
    return () => { cancelled = true }
  }, [session?.id])

  const addExtra = async (exerciseId: number) => {
    if (!session) return
    setExtras((prev) => {
      if (prev.includes(exerciseId)) return prev
      const next = [...prev, exerciseId]
      void setMeta(extrasKey(session.id), next)
      return next
    })
  }

  /** Stop showing this exercise in today's workout, and drop what it holds. */
  const removeFromWorkout = async (exerciseId: number) => {
    if (!session) return

    // The sets go with it. Leaving them would put the exercise straight back on
    // screen under "This workout" and keep it in every total for the session —
    // removed from the queue but not from the workout, which is worse than not
    // offering this at all. WorkoutQueue has already confirmed when there are any.
    const doomed = sets.filter((s) => s.exercise_id === exerciseId)
    for (const s of doomed) await deleteSet(s.id)

    setRemoved((prev) => {
      if (prev.includes(exerciseId)) return prev
      const next = [...prev, exerciseId]
      void setMeta(removedKey(session.id), next)
      return next
    })

    // Don't leave the panel open on something no longer in the workout. Clearing
    // the selection hands it back to the effect above, which lands on the next
    // exercise still standing, or the picker if there isn't one.
    if (currentExerciseId === exerciseId) setCurrentExerciseId(null)
    if (doomed.length) syncSoon()
  }

  const reorderWorkout = async (ids: number[]) => {
    if (!session) return
    setOrder((prev) => {
      const next = mergeOrder(prev, ids)
      void setMeta(orderKey(session.id), next)
      return next
    })
  }

  const restoreToWorkout = async (exerciseId: number) => {
    if (!session) return
    setRemoved((prev) => {
      const next = prev.filter((id) => id !== exerciseId)
      void setMeta(removedKey(session.id), next)
      return next
    })
  }

  // undefined = the Dexie query hasn't resolved yet. null = it resolved and
  // there is no workout in progress. Conflating them is what made this screen
  // sit on "Loading…" forever — see the sentinel note in db/queries.ts.
  if (session === undefined) return <StuckLoading />

  if (session === null) {
    // One-off workouts are plans in the database so they inherit the whole
    // prescription machinery, but they belong to a date on the calendar rather
    // than to this list of programmes you can start any time.
    const planned = plans
      .filter((plan) => plan.source !== 'oneoff')
      .flatMap((plan) => plan.days.map((day) => ({ plan, day })))

    return (
      <div className="card">
        <h2 style={{ marginTop: 0 }}>No workout in progress</h2>
        <p className="muted">
          Start one and log as you go. It saves to this device immediately, so
          it works with no signal.
        </p>

        <label htmlFor="session-date">Date</label>
        <input
          id="session-date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        {startDate !== todayIso() && (
          <p className="muted" style={{ fontSize: '.8rem', margin: '.2rem 0 .5rem' }}>
            {startDate < todayIso()
              ? 'Recording a past workout — the one you did without your phone.'
              : 'Dated ahead — sets will be saved against that day.'}
          </p>
        )}

        <button
          onClick={async () => {
            await startSession({
              title: defaultTitle(),
              started_at: startedAtFor(startDate),
            })
            syncSoon()
          }}
        >
          {startDate === todayIso() ? 'Start workout' : 'Record that workout'}
        </button>

        {/* Only for today. Everything the suggestion reasons from — what you
            trained in the last 48 hours, how long it has been, what is stale —
            is about now, and there is nothing to decide about a session you
            have already done and are typing up. */}
        {startDate === todayIso() && (
          <SuggestWorkout
            exercises={exercises}
            unit={unit}
            distancePref={distancePref}
            date={startDate}
            onStart={async ({ planId, planDayId, title }) => {
              await startSession({
                title,
                plan_id: planId,
                plan_day_id: planDayId,
                started_at: startedAtFor(startDate),
              })
              syncSoon()
            }}
          />
        )}

        {planned.length > 0 && (
          <>
            <p className="muted" style={{ margin: '1rem 0 .4rem', fontSize: '.85rem' }}>
              Or start from a plan — the exercises come pre-loaded, in order:
            </p>
            <ul className="plain" style={{ margin: 0 }}>
              {planned.map(({ plan, day }) => (
                <li key={day.id}>
                  <button
                    className="secondary"
                    style={{ width: '100%', textAlign: 'left', marginTop: '.35rem' }}
                    onClick={async () => {
                      await startSession({
                        title: day.name,
                        plan_id: plan.id,
                        plan_day_id: day.id,
                        started_at: startedAtFor(startDate),
                      })
                      syncSoon()
                    }}
                  >
                    <strong>{day.name}</strong>
                    <span className="muted" style={{ fontSize: '.85rem' }}>
                      {' · '}{plan.name}
                      {' · '}{day.items.length} exercise{day.items.length === 1 ? '' : 's'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    )
  }

  const currentExercise = currentExerciseId ? byId.get(currentExerciseId) : null
  const currentSets = sets.filter((s) => s.exercise_id === currentExerciseId)
  // What the plan asks of this exercise today, if anything. Null for an exercise
  // added on the day, and for a workout that didn't come from a plan at all.
  //
  // A plain find, NOT useMemo. Everything below this point sits after two early
  // returns — `session === undefined` while Dexie resolves, `session === null`
  // when no workout is open — so a hook here runs on some renders and not others.
  // Starting a workout then changes the hook count between renders, which is
  // React error #310 and a blank screen. A find over at most a dozen items was
  // never worth memoising in the first place.
  const prescription =
    planDay?.items.find((i) => i.exercise?.id === currentExerciseId) ?? null

  // Sets belong to the day of the workout, not the day it was typed in.
  // Without this a Saturday session recorded on Monday would put its sets on
  // Monday, and every chart, PR date and calendar square would believe them.
  //
  // "Not today" rather than "before today": a session dated forward has the
  // same problem in the other direction, and the calendar can create one.
  // Both halves matter: an exercise you added but haven't logged yet, and one
  // you logged straight from the picker without going through Add.
  // `removed` hides, it does not reclassify — see the invariant in sessionQueue,
  // which is where these rules live and are tested.
  const extraIds = applyOrder(
    extraExerciseIds({
      extras,
      setExerciseIds: sets.map((s) => s.exercise_id),
      prescribedIds: prescribedIdsOf(planDay?.items ?? []),
      removed,
    }),
    (id) => id,
    order,
  )

  const sessionDay = dayOf(session.started_at)
  const isOtherDay = sessionDay !== todayIso()
  const performedAt = isOtherDay ? session.started_at : undefined

  return (
    <>
      <div className="row" style={{ marginBottom: '.5rem' }}>
        <div>
          <strong>{session.title || 'Workout'}</strong>
          <div className="muted">{formatClock(session.started_at)}</div>
        </div>
        <button
          className="secondary"
          style={{ marginTop: 0 }}
          onClick={async () => {
            await finishSession(session.id)
            setCurrentExerciseId(null)
            // The OS alarm outlives this screen, so the workout ending has to
            // retract it explicitly — otherwise "Rest over" arrives two minutes
            // after you've left the gym. Deliberately not done on unmount:
            // logging a set remounts this component, and an unmount-cancel
            // racing the new mount's schedule would cancel the timer it just
            // set, both being notification id 1.
            void notify.cancelRestDone()
            syncSoon(0)
          }}
        >
          Finish
        </button>
      </div>

      {isOtherDay && (
        <div
          style={{
            padding: '.5rem .7rem',
            margin: '.5rem 0',
            background: 'var(--surface-2)',
            borderRadius: 'var(--radius)',
            fontSize: '.85rem',
          }}
        >
          Recording <strong>{formatDay(session.started_at)}</strong> — sets are
          saved against that day, not today.
        </div>
      )}

      {/* restKey only leaves 0 once a set has been logged, so the first mount
          of a fresh workout leaves the timer idle rather than counting.
          A session dated to another day skips it entirely: you aren't resting
          between sets you finished on Saturday, and an alarm two minutes into
          typing up an old workout is pure noise. */}
      {!isOtherDay && (
        <RestTimer key={restKey} seconds={defaultRest} autoStart={restKey > 0} />
      )}

      {/* Not just for plans. Without one the card is the added list on its own,
          which is what makes a freestyle workout a workout with several
          exercises in it rather than one panel you keep overwriting.

          Held back until there's something in it: on a workout that has only
          just started the picker is already the whole screen, and an empty card
          whose only content is "Add an exercise" sitting above a list of every
          exercise is a second, worse copy of the same button. */}
      {(planDay || extraIds.length > 0 || removed.length > 0) && (
        <WorkoutQueue
          day={planDay}
          sets={sets}
          unit={unit}
          distancePref={distancePref}
          currentExerciseId={currentExerciseId}
          extras={extraIds}
          removed={removed}
          order={order}
          exercisesById={byId}
          onPick={(id) => {
            setRevealTick((t) => t + 1)
            setCurrentExerciseId(id)
            setPicking(false)
          }}
          onAdd={() => setPicking(true)}
          onRemove={(id) => void removeFromWorkout(id)}
          onRestore={(id) => void restoreToWorkout(id)}
          onReorder={(ids) => void reorderWorkout(ids)}
        />
      )}

      {/* scroll-margin-top so `block: 'start'` clears the pinned rest timer rather
          than parking the panel's heading behind it. */}
      <div
        ref={panelRef}
        style={{ scrollMarginTop: 'calc(env(safe-area-inset-top, 0px) + 4.75rem)' }}
      >
      {picking || !currentExercise ? (
        <ExercisePicker
          exercises={exercises}
          onPick={(id) => {
            const prescribed = planDay?.items.some((i) => i.exercise?.id === id)
            // Regardless of whether there's a plan. A workout without one used
            // to record nothing here, so an exercise you selected but hadn't
            // logged a set against yet existed only as the open panel — hit
            // Change and it was gone, with no list to have left it in.
            if (!prescribed) void addExtra(id)
            // Choosing something you removed earlier is a change of mind, not a
            // second kind of exercise. Without this it would be selected and
            // still hidden from the queue — the panel open on a lift the list
            // says isn't in the workout.
            if (removed.includes(id)) void restoreToWorkout(id)
            setRevealTick((t) => t + 1)
            setCurrentExerciseId(id)
            setPicking(false)
          }}
          onCancel={currentExercise ? () => setPicking(false) : undefined}
        />
      ) : (
        <ExercisePanel
          exercise={currentExercise}
          sessionId={session.id}
          sets={currentSets}
          unit={unit}
          distancePref={distancePref}
          prescription={prescription}
          performedAt={performedAt}
          onChangeExercise={() => setPicking(true)}
          onLogged={() => setRestKey((k) => k + 1)}
          username={username}
        />
      )}
      </div>

      {sets.length > 0 && (
        <>
          <h2>This workout</h2>
          <ul className="plain">
            {groupByExercise(sets).map(([exerciseId, group]) => (
              <li key={exerciseId} style={{ display: 'block' }}>
                <div className="row">
                  <strong>{byId.get(exerciseId)?.name ?? 'Unknown'}</strong>
                  <span className="muted">
                    {/* A left and a right are one set — see lib/rounds. */}
                    {countRounds(group)} set{countRounds(group) === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="muted" style={{ marginTop: '.2rem' }}>
                  {groupRounds(group)
                    .map((round) => formatRound(round, (s) => formatSet(s, byId.get(exerciseId), unit, {
                      withRpe: false,
                      du: unitFor(byId.get(exerciseId), { unit, pref: distancePref, overrides }),
                    })))
                    .join('  ·  ')}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  )
}

/**
 * Loading state that admits when it is stuck.
 *
 * A local-database read should resolve in milliseconds. If it hasn't after a
 * few seconds, something is wrong — IndexedDB unavailable, a blocked upgrade,
 * private-browsing storage — and none of those raise an error a user would
 * ever see. An indefinite spinner is the worst possible reporting of that:
 * it looks like progress. Say so, and offer the escape hatch.
 */
function StuckLoading() {
  const [stuck, setStuck] = useState(false)

  useEffect(() => {
    const id = setTimeout(() => setStuck(true), 6000)
    return () => clearTimeout(id)
  }, [])

  if (!stuck) return <p className="muted">Loading…</p>

  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Still loading</h2>
      <p className="muted">
        Lift can't read its local database on this device. That's usually
        private browsing, or storage being blocked or full.
      </p>
      <p className="muted">
        Your workouts on the server are safe. Try More &rarr; Reset local data,
        or reopen the app.
      </p>
      <button onClick={() => window.location.reload()}>Reload</button>
    </div>
  )
}


/** What the measure boxes hold, as typed. One of these per side on a
 *  one-sided exercise. */
type Boxes = { weight: string; reps: string; duration: string; distance: string }

const EMPTY_BOXES: Boxes = { weight: '', reps: '', duration: '', distance: '' }

function ExercisePanel({
  exercise,
  sessionId,
  sets,
  unit,
  distancePref,
  prescription,
  performedAt,
  onChangeExercise,
  onLogged,
  username,
}: {
  exercise: Exercise
  sessionId: string
  sets: SetRow[]
  unit: Unit
  distancePref: DistancePref
  /** This exercise's plan item, when the workout came from a plan. */
  prescription?: PlanItem | null
  /** Set when the workout is backdated; undefined means "stamp it now". */
  performedAt?: string
  onChangeExercise: () => void
  onLogged: () => void
  username: string
}) {
  const last = useLiveQuery(
    () => lastPerformance(exercise.id, sessionId),
    [exercise.id, sessionId],
  )

  const suggestion = useMemo(
    () =>
      suggestNext(last?.sets ?? null, {
        incrementKg: exercise.increment_kg,
        barKg: exercise.equipment === 'barbell' ? 20 : 0,
        // The stack subtracts, so every direction in the rule flips. Without
        // this the app answered a cleared rep range on an assist machine by
        // adding help — advising you to make the lift easier.
        assisted: weightIsAssist(exercise),
        // So the answer is a weight this gym can be set to. See ProgressionOpts.
        unit,
        // The prescription belongs inside the rule, not layered over its answer.
        // These options existed from the start and nothing ever passed them, so
        // double progression ran against a hardcoded 5-8 band: on a plan of
        // 3 × 10 it advised "hit 8 on every set — add weight and reset to 5",
        // directly under a prescription saying 10. Feeding the band fixes the
        // suggested reps and the sentence explaining them at the same time.
        ...(repsBand(prescription) ?? {}),
      }),
    [last, exercise, prescription],
  )

  const [weight, setWeight] = useState('')
  const [reps, setReps] = useState('')
  const [duration, setDuration] = useState('')
  const [distance, setDistance] = useState('')
  const [warmup, setWarmup] = useState(false)
  const [straps, setStraps] = useState<'strap' | 'no-strap'>('no-strap')
  const [note, setNote] = useState('')
  const [touched, setTouched] = useState(false)
  // Which logged set is open for editing. One at a time: two open forms on a
  // phone is more scrolling than the screen can spare mid-workout.
  const [editing, setEditing] = useState<string | null>(null)
  // Whether the "how to do it" description is expanded. Collapsed by default:
  // once you know the lift it's noise between you and the number pad.
  const [info, setInfo] = useState(false)

  // This panel is re-rendered with new props rather than remounted when you
  // switch exercises, so an open editor would survive the switch and reopen the
  // moment you came back to it — on a set you had stopped thinking about. The
  // description is closed for the same reason: it described the last exercise.
  useEffect(() => {
    setEditing(null)
    setInfo(false)
  }, [exercise.id])

  const ms = measuresOf(exercise)
  const takesWeight = ms.includes('weight')
  const takesReps = ms.includes('reps')
  const takesTime = ms.includes('time')
  const takesDistance = ms.includes('distance')
  const scale = distanceScaleOf(exercise)
  const addedLoad = weightIsAdded(exercise)
  const assistLoad = weightIsAssist(exercise)
  // What number to type, when the boxes alone cannot say — how many sides, and
  // how many dumbbells. See lib/entryHint.
  const hint = entryHint(exercise)
  // One-sided and loaded: a row of boxes per side, and "Log set" writes both
  // as one set that only counts once both sides are in. See lib/rounds. The
  // left side uses the same state the single row always has.
  const sided = logsSides(exercise, ms)
  const [right, setRight] = useState<Boxes>(EMPTY_BOXES)
  const overrides = useDistanceOverrides()
  const du = unitFor(exercise, { unit, pref: distancePref, overrides })

  // What the boxes should say if nobody has typed in them. Precedence — the
  // plan first, then the last set actually performed, then nothing; except
  // reps mid-exercise, where today's previous working set beats the plan —
  // lives in lib/prefill. `sets` includes warm-ups and prefill skips them.
  // Memoised rather than computed inside the effect because the voice logger
  // needs the same answer.
  //
  // Per side on a one-sided exercise: the left boxes carry from the last left,
  // the right from the last right — a weaker side's 8 reps must not be
  // prefilled from the stronger side's 10.
  const prefillFor = (side?: Side) =>
    prefillFrom({
      lastSet: side ? carryFor(sets, side) : sets[sets.length - 1] ?? null,
      // The exercise's own history: how it finished the last time it was
      // done. `lastPerformance` has already dropped warm-ups and sorted by
      // set index, so this is the last working set of that session.
      history: side ? carryFor(last?.sets, side) : last?.sets[last.sets.length - 1] ?? null,
      prescription,
      unit,
      incrementKg: exercise.increment_kg,
      du,
    })
  const prefill = useMemo(
    () => prefillFor(sided ? 'L' : undefined),
    [sets, last, prescription, unit, exercise.increment_kg, du, sided],
  )
  const prefillRight = useMemo(
    () => (sided ? prefillFor('R') : null),
    [sets, last, prescription, unit, exercise.increment_kg, du, sided],
  )

  // Apply it, but stop overriding once the user types — silently replacing what
  // someone just entered is infuriating.
  useEffect(() => {
    if (touched) return
    setWeight(prefill.weight)
    setReps(prefill.reps)
    setDuration(prefill.duration)
    setDistance(prefill.distance)
    setRight(prefillRight
      ? { weight: prefillRight.weight, reps: prefillRight.reps,
          duration: prefillRight.duration, distance: prefillRight.distance }
      : EMPTY_BOXES)
  }, [prefill, prefillRight, touched])

  const weightKg = displayToKg(parseFloat(weight), unit)
  const durationSeconds = parseDuration(duration)
  const distanceM = displayToMetres(parseFloat(distance), du)
  const weightKgRight = displayToKg(parseFloat(right.weight), unit)
  const durationSecondsRight = parseDuration(right.duration)
  const distanceMRight = displayToMetres(parseFloat(right.distance), du)
  // The person's OWN bar and plates. Called with the defaults this computed a
  // 20 kg bar and metric plates for everybody, so a pounds lifter loading three
  // 45s was told "Per side: 44.1 (approx)" — the 45 lb plate the app had a
  // table for all along, converted from the 20 kg one it used instead.
  const gym = gymFor(unit)
  // `bar_is_standard === false` is the EZ bar and the landmines: one has no
  // knowable bar weight, the other loads a single end. The breakdown is absent
  // there rather than wrong, and `entryHint` says why.
  const plates =
    exercise.equipment === 'barbell'
      && exercise.bar_is_standard !== false
      && weightKg
      ? platesFor(weightKg, gym.barKg, gym.plates)
      : null

  // What has to be present before "Log set" means anything. Reps for a squat,
  // seconds for a plank, metres for a row — the primary measure of the mode.
  const enough = (repsText: string, metres: number | null, secs: number | null,
                  kg: number | null) =>
    takesReps
      ? Boolean(repsText)
      : takesDistance
        ? Boolean(metres)
        : takesTime
          ? Boolean(secs)
          : Boolean(kg)
  // Both sides, on a one-sided exercise. Half a set is not a set: letting one
  // side through would leave a round the plan can never count as done.
  const canLog = enough(reps, distanceM, durationSeconds, weightKg)
    && (!sided || enough(right.reps, distanceMRight, durationSecondsRight, weightKgRight))

  type Setter = (field: keyof Boxes, value: string) => void

  const setLeft: Setter = (field, value) => {
    setTouched(true)
    if (field === 'weight') {
      // The same dumbbell usually goes in the other hand, so the right box
      // follows the left until somebody types a different number into it.
      if (sided) setRight((r) => (r.weight === weight ? { ...r, weight: value } : r))
      setWeight(value)
    } else if (field === 'reps') setReps(value)
    else if (field === 'duration') setDuration(value)
    else setDistance(value)
  }

  const setRightBox: Setter = (field, value) => {
    setTouched(true)
    setRight((r) => ({ ...r, [field]: value }))
  }

  const leftBoxes: Boxes = { weight, reps, duration, distance }
  const blurLeft = () => setDuration((d) => normalizeDuration(d))
  const blurRight = () => setRight((r) => ({ ...r, duration: normalizeDuration(r.duration) }))

  /**
   * One row of measure boxes: once normally, and once per side on a one-sided
   * exercise. `side` keeps the input ids unique and puts the distance unit
   * toggle on the first row only — both sides share one unit, and a toggle per
   * row would invite converting one side and not the other.
   */
  const boxes = (v: Boxes, set: Setter, onDurationBlur: () => void, side?: Side) => {
    const id = (name: string) => (side ? `${name}-${side}` : name)
    return (
      <div style={{ display: 'flex', gap: '.75rem' }}>
        {takesWeight && (
          <div style={{ flex: 1 }}>
            <label htmlFor={id('weight')}>
              {/* Four different meanings, and saying which one costs a word.
                  "Added" is the one that was missing: on a pull up or a dip the
                  box is a dip belt, not the load, and calling it Weight the
                  same as a bench press left blank as the only safe answer.
                  "Assist" is the one that ran backwards — the stack takes weight
                  OFF, so the number here is help, and a bigger one is an easier
                  set. */}
              {assistLoad
                ? `Assist (${unit})`
                : addedLoad
                ? `Added (${unit})`
                : takesReps ? `Weight (${unit})` : `Load (${unit})`}
            </label>
            <input
              id={id('weight')}
              type="number"
              inputMode="decimal"
              step="any"
              value={v.weight}
              onChange={(e) => set('weight', e.target.value)}
            />
          </div>
        )}
        {takesReps && (
          <div style={{ flex: 1 }}>
            <label htmlFor={id('reps')}>Reps</label>
            <input
              id={id('reps')}
              type="number"
              inputMode="numeric"
              value={v.reps}
              onChange={(e) => set('reps', e.target.value)}
            />
          </div>
        )}
        {takesDistance && (
          <div style={{ flex: 1 }}>
            {/* No space-between: pushed to the far edge of its column the
                toggle sits right next to the Time label and reads as if it
                belongs to that box instead. Kept snug against the word it
                qualifies. */}
            <label htmlFor={id('distance')} style={{ display: 'flex', alignItems: 'baseline',
                                                     gap: '.4rem' }}>
              <span>Distance</span>
              {/* The unit is a control, not a caption. Sprints in yards and
                  jumps in feet is a reasonable thing to want and no single
                  account-wide answer can express it, so the choice lives next
                  to the box it governs and is remembered per exercise. */}
              {side !== 'R' && (
                <DistanceUnitToggle
                  options={DISTANCE_UNITS[scale]}
                  value={du}
                  onChange={(next) => {
                    // Convert what is already typed rather than reinterpreting
                    // it. Leaving "40" alone while relabelling yd -> m silently
                    // turns a 40 yard sprint into a 40 metre one. Both sides,
                    // when there are two: they share the unit.
                    const convert = (typed: string) => {
                      const metres = displayToMetres(parseFloat(typed), du)
                      return metres === null ? null : String(metresToDisplay(metres, next) ?? '')
                    }
                    const leftNext = convert(distance)
                    const rightNext = sided ? convert(right.distance) : null
                    void setDistanceOverride(exercise.id, next)
                    if (leftNext !== null) {
                      setTouched(true)
                      setDistance(leftNext)
                    }
                    if (rightNext !== null) {
                      setTouched(true)
                      setRight((r) => ({ ...r, distance: rightNext }))
                    }
                  }}
                />
              )}
            </label>
            <input
              id={id('distance')}
              type="number"
              inputMode="decimal"
              step="any"
              value={v.distance}
              onChange={(e) => set('distance', e.target.value)}
            />
          </div>
        )}
        {takesTime && (
          <div style={{ flex: 1 }}>
            <label htmlFor={id('duration')}>Time</label>
            {/* Text, not number: "1:30" is how people say it and a number input
                can't hold a colon. But neither can a phone keypad, so the colon
                is placed for you as the digits arrive — 1-3-0 is 1:30 — and one
                or two digits still mean plain seconds. */}
            <input
              id={id('duration')}
              type="text"
              inputMode="numeric"
              placeholder="45 or 1:30"
              value={v.duration}
              onChange={(e) => set('duration', maskDuration(e.target.value))}
              onBlur={onDurationBlur}
            />
            <div className="muted" style={{ marginTop: '.2rem', fontSize: '.78rem' }}>
              Type 130 for 1:30
            </div>
          </div>
        )}
      </div>
    )
  }

  /** The set as a button that opens its editor. */
  const editButton = (s: SetRow, side?: Side) => (
    // The set itself is the control. Mistyping a set is constant —
    // 22 for 225, the wrong box with cold hands — and the only
    // repair used to be Remove and re-log, which threw away the
    // set's place in the workout and its note along with the typo.
    // A separate Edit button would be a third control on a row
    // already carrying two; the row is the obvious thing to press.
    <button
      key={s.id}
      className="secondary"
      aria-label={`Edit set ${s.set_index}${side ? ` ${SIDE_LABEL[side].toLowerCase()}` : ''}`}
      style={{
        flex: 1, textAlign: 'left', marginTop: 0,
        padding: '.2rem .3rem', minHeight: 0,
        outline: editing === s.id ? '2px solid var(--accent)' : undefined,
      }}
      onClick={() => setEditing(editing === s.id ? null : s.id)}
    >
      <span>
        {s.is_warmup && <span className="muted">warmup · </span>}
        {side && <span className="muted">{SIDE_LABEL[side]} · </span>}
        {formatSet(s, exercise, unit, { withRpe: false, withWarmup: false, du })}
        {/* A span, not a div: this sits inside a button now, and a
            button only takes phrasing content. */}
        {s.notes && (
          <span className="muted"
                style={{ display: 'block', fontSize: '.8rem' }}>{s.notes}</span>
        )}
      </span>
    </button>
  )

  /** Remove, for a set or for both sides of one at once. */
  const removeButton = (rows: SetRow[]) => (
    <button
      className="secondary"
      style={{ marginTop: 0, padding: '.2rem .5rem', minHeight: 0, fontSize: '.8rem',
               whiteSpace: 'nowrap' }}
      onClick={async () => {
        if (rows.some((r) => r.id === editing)) setEditing(null)
        for (const r of rows) await deleteSet(r.id)
        syncSoon()
      }}
    >
      Remove
    </button>
  )

  const editor = (s: SetRow) => editing === s.id && (
    <SetEditor
      key={`edit-${s.id}`}
      set={s}
      measures={ms}
      unit={unit}
      du={du}
      addedLoad={addedLoad}
      onCancel={() => setEditing(null)}
      onSave={async (patch) => {
        await updateSet(s.id, patch)
        setEditing(null)
        syncSoon()
      }}
    />
  )

  return (
    <div className="card">
      <div className="row">
        <strong>{exercise.name}</strong>
        <span style={{ display: 'flex', gap: '.4rem' }}>
          {/* Spelled out rather than the queue's "?", because here there is
              room for words and this is the panel you're standing in front of
              when you realise you don't know the lift. */}
          {hasInfo(exercise) && (
            <button
              className="secondary"
              style={{ marginTop: 0 }}
              aria-expanded={info}
              onClick={() => setInfo((was) => !was)}
            >
              {info ? 'Hide' : 'How to do it'}
            </button>
          )}
          <button className="secondary" style={{ marginTop: 0 }} onClick={onChangeExercise}>
            Change
          </button>
        </span>
      </div>

      {strapsFor(username, exercise) && (
        <>
          <div role="radiogroup" aria-label="Straps"
               style={{ display: 'flex', gap: '1.25rem', marginTop: '.6rem' }}>
            {([['strap', 'Strap'], ['no-strap', 'No-strap']] as const).map(([value, label]) => (
              <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
                <input
                  type="radio"
                  name={`straps-${exercise.id}`}
                  checked={straps === value}
                  onChange={() => setStraps(value)}
                  style={{ width: 'auto' }}
                />
                {label}
              </label>
            ))}
          </div>
          {straps === 'strap' && <div className="error">You bitch.</div>}
        </>
      )}

      {info && <ExerciseInfoPanel exercise={exercise} />}

      <div className="muted" style={{ marginTop: '.4rem' }}>
        {last ? (
          <>
            Last time ({relativeDate(last.performedAt)}):{' '}
            {hasSides(last.sets)
              // "3 × 10 @ 20" would count each side as a set of its own.
              ? groupRounds(last.sets)
                  .map((round) => formatRound(round, (s) =>
                    formatSet(s, exercise, unit, { withRpe: false, du })))
                  .join(', ')
              : takesWeight && takesReps
              ? describeSets(last.sets, (kg) => formatWeight(kg, unit), assistLoad)
              : last.sets
                  .map((s) => formatSet(s, exercise, unit, { withRpe: false, du }))
                  .join(', ')}
          </>
        ) : (
          'No history for this exercise yet.'
        )}

        {/* Notes from that session, under the numbers rather than folded into
            them: "set 3: very hard" is the reason you'd pick a weight, but it
            doesn't belong inside a 3x10 @ 20 lb summary. Only the sets that
            actually carried a note — the rest would be blank lines. */}
        {last?.sets.some((s) => s.notes) && (
          <div style={{ marginTop: '.25rem', fontSize: '.85rem', fontStyle: 'italic' }}>
            {last.sets
              .filter((s) => s.notes)
              .map((s) => (
                <div key={s.id}>
                  Set {s.set_index}: {s.notes}
                </div>
              ))}
          </div>
        )}
      </div>

      {/* The progression rule reasons about load and reps, so it has nothing
          to say about a plank or a row. Showing it anyway would be advice
          about a lift you aren't doing. */}
      {takesWeight && takesReps && (
        <div
          style={{
            marginTop: '.6rem',
            padding: '.5rem .7rem',
            background: 'var(--surface-2)',
            borderRadius: 'var(--radius)',
            fontSize: '.9rem',
          }}
        >
          {suggestion.reason}
        </div>
      )}

      {sided
        ? SIDES.map((side) => (
            <div key={side} style={{ marginTop: '.5rem' }}>
              <div style={{ fontSize: '.85rem', fontWeight: 600 }}>{SIDE_LABEL[side]}</div>
              {side === 'L'
                ? boxes(leftBoxes, setLeft, blurLeft, side)
                : boxes(right, setRightBox, blurRight, side)}
            </div>
          ))
        : boxes(leftBoxes, setLeft, blurLeft)}

      {/* Once, under both boxes, rather than in each label — "Weight (lb) per
          side" wraps on a phone and knocks the two columns out of line. */}
      {hint && takesWeight && (
        <div className="muted" style={{ marginTop: '.3rem', fontSize: '.85rem' }}>
          {hint}
        </div>
      )}

      {takesTime
        && [duration, ...(sided ? [right.duration] : [])]
             .some((d) => d.trim() !== '' && parseDuration(d) === null) && (
        <div className="muted" style={{ marginTop: '.3rem', fontSize: '.85rem' }}>
          Enter seconds (45) or minutes:seconds (1:30).
        </div>
      )}

      {plates && plates.perSide.length > 0 && (
        <div className="muted" style={{ marginTop: '.5rem' }}>
          Per side: {plates.perSide.map((p) => formatWeight(p, unit, { withUnit: false })).join(' + ')}
          {plates.remainderKg > 0 && ' (approx)'}
        </div>
      )}

      {/* Attached to the set, because that's the thing that happened. A note
          about how the second set felt is worthless filed under the whole
          workout, and the schema has carried per-set notes from the start —
          nothing ever offered a way to write one. */}
      <input
        type="text"
        placeholder="Note — felt heavy, left knee, belt on…"
        value={note}
        style={{ marginTop: '.6rem', fontSize: '.9rem' }}
        onChange={(e) => setNote(e.target.value)}
      />

      <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', margin: '.8rem 0 0' }}>
        <input
          type="checkbox"
          checked={warmup}
          onChange={(e) => setWarmup(e.target.checked)}
          style={{ width: 'auto' }}
        />
        Warmup set
      </label>

      <button
        style={{ width: '100%' }}
        disabled={!canLog}
        onClick={async () => {
          // Only ever write the measures this exercise takes. Letting a
          // stale field through would put a weight on a plank purely because
          // the input had been visible for a different exercise a moment ago.
          const measured = (kg: number | null, repsText: string,
                            secs: number | null, metres: number | null) => ({
            weight_kg: takesWeight ? kg : null,
            reps: takesReps ? parseInt(repsText, 10) || null : null,
            duration_seconds: takesTime ? secs : null,
            distance_m: takesDistance ? metres : null,
          })
          const shared = {
            session_id: sessionId,
            exercise_id: exercise.id,
            performed_at: performedAt,
            is_warmup: warmup,
            notes: note.trim() || null,
          }
          if (sided) {
            await addRound(shared,
              measured(weightKg, reps, durationSeconds, distanceM),
              measured(weightKgRight, right.reps, durationSecondsRight, distanceMRight))
          } else {
            await addSet({ ...shared, ...measured(weightKg, reps, durationSeconds, distanceM) })
          }
          // Cleared, not carried: a note describes the set you just did, and
          // silently stamping "felt heavy" onto the next four would turn it
          // into noise nobody trusts.
          setNote('')
          setTouched(false)
          onLogged()
          syncSoon()
        }}
      >
        Log set {countRounds(sets, { working: true }) + 1}
      </button>


      {sets.length > 0 && (
        <ul className="plain" style={{ marginTop: '1rem' }}>
          {groupRounds(sets).map((round) => (
            <li key={round.key} style={{ display: 'block' }}>
              {round.single ? (
                <div className="row">
                  {editButton(round.single)}
                  {removeButton([round.single])}
                </div>
              ) : (
                // Both sides of one set, stacked, with one Remove: taking out
                // one side would leave a set that can never count as done.
                <div className="row" style={{ alignItems: 'stretch' }}>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column',
                                gap: '.2rem' }}>
                    {SIDES.map((side) => {
                      const s = side === 'L' ? round.left : round.right
                      return s ? editButton(s, side) : (
                        <span key={side} className="muted"
                              style={{ padding: '.2rem .3rem', fontSize: '.9rem' }}>
                          {SIDE_LABEL[side]} · not logged
                        </span>
                      )
                    })}
                  </div>
                  {removeButton(rowsOf(round))}
                </div>
              )}
              {rowsOf(round).map(editor)}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** A private joke for two lifters: deadlifts get a strap / no-strap choice. */
function strapsFor(username: string, exercise: Exercise): boolean {
  return ['chucklifts', 'wes'].includes(username.toLowerCase()) && /deadlift/i.test(exercise.name)
}

function groupByExercise(sets: SetRow[]): [number, SetRow[]][] {
  const order: number[] = []
  const groups = new Map<number, SetRow[]>()
  for (const s of sets) {
    if (!groups.has(s.exercise_id)) {
      groups.set(s.exercise_id, [])
      order.push(s.exercise_id)
    }
    groups.get(s.exercise_id)!.push(s)
  }
  return order.map((id) => [id, groups.get(id)!])
}

/** Local-only, per session. Never synced — see the note where extras live. */
function extrasKey(sessionId: string): string {
  return `lift.session.extras.${sessionId}`
}

function removedKey(sessionId: string): string {
  return `lift.session.removed.${sessionId}`
}

function orderKey(sessionId: string): string {
  return `lift.session.order.${sessionId}`
}

function defaultTitle(): string {
  const h = new Date().getHours()
  const part = h < 11 ? 'Morning' : h < 17 ? 'Afternoon' : 'Evening'
  return `${part} workout`
}

function formatClock(iso: string): string {
  return parseIso(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}


function formatDay(iso: string): string {
  return parseIso(iso).toLocaleDateString([], {
    weekday: 'long', month: 'short', day: 'numeric',
  })
}

function relativeDate(iso: string): string {
  const days = Math.floor((Date.now() - parseIso(iso).getTime()) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 14) return `${days} days ago`
  return parseIso(iso).toLocaleDateString([], { month: 'short', day: 'numeric' })
}


/**
 * The unit a distance is typed in — two or three tiny buttons beside the box.
 *
 * A control rather than a setting, because the right answer differs per
 * exercise: yards for a sprint, feet for a broad jump, metres for a carry, and
 * whatever you like for a 5 km row. It writes through to the per-exercise
 * override, so it is a choice made once and then remembered.
 */
function DistanceUnitToggle({
  options,
  value,
  onChange,
}: {
  options: DistanceUnit[]
  value: DistanceUnit
  onChange: (du: DistanceUnit) => void
}) {
  return (
    <span style={{ display: 'inline-flex', gap: '.2rem' }}>
      {options.map((o) => (
        <button
          key={o}
          type="button"
          // grid-cell opts out of the global expanded touch target
          // (button::after { inset: -9px -2px }): these sit a few px apart, so
          // the areas would overlap and the last one drawn would swallow taps
          // meant for its neighbour — the calendar bug, in miniature.
          className={o === value ? 'grid-cell' : 'secondary grid-cell'}
          aria-pressed={o === value}
          aria-label={`Type distance in ${o}`}
          style={{
            marginTop: 0, padding: '.1rem .45rem', minHeight: 0,
            fontSize: '.75rem', lineHeight: 1.6,
          }}
          onClick={() => onChange(o)}
        >
          {o}
        </button>
      ))}
    </span>
  )
}
